import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../Firebase';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { 
  Plus, Edit2, Trash2, Save, X, Search, PiggyBank, Calendar, Clock, Smile, Baby, 
  Wallet, Landmark, Briefcase, Gem, TrendingUp, AlertCircle
} from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';

// Available icons to choose from
const iconOptions = [
  { name: 'PiggyBank', component: PiggyBank },
  { name: 'Calendar', component: Calendar },
  { name: 'Clock', component: Clock },
  { name: 'Smile', component: Smile },
  { name: 'Baby', component: Baby },
  { name: 'Wallet', component: Wallet },
  { name: 'Landmark', component: Landmark },
  { name: 'Briefcase', component: Briefcase },
  { name: 'Gem', component: Gem },
  { name: 'TrendingUp', component: TrendingUp },
];

const colorOptions = [
  { name: 'Primary Blue', value: 'bg-primary' },
  { name: 'Dark Blue', value: 'bg-primary-dark' },
  { name: 'Golden Secondary', value: 'bg-secondary' },
  { name: 'Accent Red', value: 'bg-accent' },
];

const DepositSchemesManager = () => {
  const [schemes, setSchemes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [langTab, setLangTab] = useState('en');

  const [formData, setFormData] = useState({
    titleEn: '',
    titleMr: '',
    interest1: '',
    period1En: '',
    period1Mr: '',
    interest2: '',
    period2En: '',
    period2Mr: '',
    descEn: '',
    descMr: '',
    iconName: 'PiggyBank',
    color: 'bg-primary',
    featuresEn: ['', '', '', ''],
    featuresMr: ['', '', '', ''],
    detailsEn: '',
    detailsMr: ''
  });

  useEffect(() => {
    const q = query(collection(db, 'deposit_schemes'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setSchemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  const resetForm = () => {
    setFormData({
      titleEn: '',
      titleMr: '',
      interest1: '',
      period1En: '',
      period1Mr: '',
      interest2: '',
      period2En: '',
      period2Mr: '',
      descEn: '',
      descMr: '',
      iconName: 'PiggyBank',
      color: 'bg-primary',
      featuresEn: ['', '', '', ''],
      featuresMr: ['', '', '', ''],
      detailsEn: '',
      detailsMr: ''
    });
    setEditingId(null);
    setLangTab('en');
    setIsModalOpen(false);
  };

  const handleFeatureEnChange = (index, value) => {
    const newFeatures = [...formData.featuresEn];
    newFeatures[index] = value;
    setFormData({ ...formData, featuresEn: newFeatures });
  };

  const handleFeatureMrChange = (index, value) => {
    const newFeatures = [...formData.featuresMr];
    newFeatures[index] = value;
    setFormData({ ...formData, featuresMr: newFeatures });
  };

  const addFeatureField = () => {
    setFormData({ 
      ...formData, 
      featuresEn: [...formData.featuresEn, ''],
      featuresMr: [...formData.featuresMr, '']
    });
  };

  const removeFeatureField = (index) => {
    const newFeaturesEn = formData.featuresEn.filter((_, i) => i !== index);
    const newFeaturesMr = formData.featuresMr.filter((_, i) => i !== index);
    setFormData({ 
      ...formData, 
      featuresEn: newFeaturesEn,
      featuresMr: newFeaturesMr
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanFeaturesEn = formData.featuresEn.filter(f => f.trim() !== '');
    const cleanFeaturesMr = formData.featuresMr.filter(f => f.trim() !== '');

    setLoading(true);

    try {
      const schemeData = {
        title: formData.titleEn, // backwards compatibility
        titleEn: formData.titleEn,
        titleMr: formData.titleMr,
        interest1: formData.interest1,
        period1: formData.period1En, // backwards compatibility
        period1En: formData.period1En,
        period1Mr: formData.period1Mr,
        interest2: formData.interest2 || '',
        period2: formData.period2En || '', // backwards compatibility
        period2En: formData.period2En || '',
        period2Mr: formData.period2Mr || '',
        interest: formData.interest1, // backwards compatibility
        period: formData.period1En,     // backwards compatibility
        desc: formData.descEn, // backwards compatibility
        descEn: formData.descEn,
        descMr: formData.descMr,
        iconName: formData.iconName,
        color: formData.color,
        features: cleanFeaturesEn, // backwards compatibility
        featuresEn: cleanFeaturesEn,
        featuresMr: cleanFeaturesMr,
        details: formData.detailsEn, // backwards compatibility
        detailsEn: formData.detailsEn,
        detailsMr: formData.detailsMr,
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, 'deposit_schemes', editingId), schemeData);
        Swal.fire('Updated!', 'Deposit scheme updated successfully.', 'success');
      } else {
        schemeData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'deposit_schemes'), schemeData);
        Swal.fire('Created!', 'New deposit scheme created successfully.', 'success');
      }
      resetForm();
    } catch (error) {
      console.error("Error saving scheme:", error);
      Swal.fire('Error', 'Failed to save scheme.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (scheme) => {
    setFormData({
      titleEn: scheme.titleEn || scheme.title || '',
      titleMr: scheme.titleMr || '',
      interest1: scheme.interest1 || scheme.interest || '',
      period1En: scheme.period1En || scheme.period1 || scheme.period || '',
      period1Mr: scheme.period1Mr || '',
      interest2: scheme.interest2 || '',
      period2En: scheme.period2En || scheme.period2 || '',
      period2Mr: scheme.period2Mr || '',
      descEn: scheme.descEn || scheme.desc || '',
      descMr: scheme.descMr || '',
      iconName: scheme.iconName || 'PiggyBank',
      color: scheme.color || 'bg-primary',
      featuresEn: scheme.featuresEn && scheme.featuresEn.length > 0 
        ? scheme.featuresEn 
        : (scheme.features && scheme.features.length > 0 ? scheme.features : ['', '', '', '']),
      featuresMr: scheme.featuresMr && scheme.featuresMr.length > 0 
        ? scheme.featuresMr 
        : ['', '', '', ''],
      detailsEn: scheme.detailsEn || scheme.details || '',
      detailsMr: scheme.detailsMr || ''
    });
    setEditingId(scheme.id);
    setLangTab('en');
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This scheme will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'deposit_schemes', id));
        Swal.fire('Deleted!', 'Scheme has been deleted.', 'success');
      } catch (error) {
        console.error("Error deleting scheme:", error);
        Swal.fire('Error', 'Failed to delete scheme.', 'error');
      }
    }
  };

  const filteredSchemes = schemes.filter(scheme => 
    (scheme.titleEn || scheme.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (scheme.titleMr || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (scheme.descEn || scheme.desc || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (scheme.descMr || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 deposit-schemes-section">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Deposit Schemes</h1>
          <p className="text-gray-500 font-medium">Manage existing schemes or create new deposit plans.</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-gray-100 shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <Search className="w-5 h-5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search schemes..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm text-gray-700 w-48"
            />
          </div>
          <button 
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="bg-[#D32F2F] text-white px-8 py-3 font-semibold rounded-full transition-all duration-300 hover:bg-[#B71C1C] hover:shadow-lg hover:shadow-red-600/30 hover:-translate-y-1 active:translate-y-0 shadow-md shadow-red-600/10 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Scheme
          </button>
        </div>
      </div>

      {/* Grid of Schemes */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredSchemes.map((scheme, index) => {
            const IconComponent = iconOptions.find(i => i.name === scheme.iconName)?.component || PiggyBank;
            
            return (
              <motion.div
                key={scheme.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all flex flex-col group"
              >
                <div className={`${scheme.color || 'bg-primary'} p-6 text-white flex justify-between items-center`}>
                  <div className="p-3 bg-white/20 rounded-2xl">
                    <IconComponent className="w-8 h-8" />
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleEdit(scheme)}
                      className="p-2 bg-white/10 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(scheme.id)}
                      className="p-2 bg-red-500/80 hover:bg-red-500 rounded-xl transition-colors backdrop-blur-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-primary-dark">{scheme.titleEn || scheme.title}</h3>
                    <div className="text-right shrink-0">
                      <div className="text-xl font-bold text-secondary leading-none">{scheme.interest1 || scheme.interest}%</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mt-1">Interest</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    <div className="scheme-opt-badge-1 px-3 py-1 rounded-lg text-xs font-bold">
                      Opt 1: {scheme.period1En || scheme.period1 || scheme.period} ({scheme.interest1 || scheme.interest}%)
                    </div>
                    {(scheme.period2En || scheme.period2) && (scheme.interest2) && (
                      <div className="scheme-opt-badge-2 px-3 py-1 rounded-lg text-xs font-bold">
                        Opt 2: {scheme.period2En || scheme.period2} ({scheme.interest2}%)
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-500 text-sm mb-6 line-clamp-2 flex-grow">
                    {scheme.descEn || scheme.desc}
                  </p>
                  
                  <div className="space-y-2 mt-auto">
                    {(scheme.featuresEn || scheme.features) && (scheme.featuresEn || scheme.features).slice(0, 3).map((feature, i) => (
                      <div key={i} className="flex items-start space-x-2 text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-dark shrink-0 mt-1.5" />
                        <span className="truncate">{feature}</span>
                      </div>
                    ))}
                    {(scheme.featuresEn || scheme.features) && (scheme.featuresEn || scheme.features).length > 3 && (
                      <div className="text-xs text-gray-400 font-medium pl-6">
                        + {(scheme.featuresEn || scheme.features).length - 3} more features
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {filteredSchemes.length === 0 && (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No deposit schemes found</h3>
          <p className="text-gray-400 mt-2">Click "Create Scheme" to add your first one.</p>
        </div>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[2.5rem] p-8 max-w-4xl w-full my-8 shadow-2xl relative"
          >
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 p-2 bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary-dark">
                {editingId ? 'Edit Deposit Scheme' : 'Create New Scheme'}
              </h2>
              <p className="text-gray-500">Fill out the details below to display on the public pages.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Language Independent Card Configuration Grid */}
              <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 space-y-6">
                <h3 className="font-extrabold text-lg text-primary-dark uppercase tracking-wider flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-secondary" />
                  General Configuration
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Card Color</label>
                    <select
                      value={formData.color}
                      onChange={(e) => setFormData({...formData, color: e.target.value})}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                    >
                      {colorOptions.map(color => (
                        <option key={color.value} value={color.value}>{color.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Interest Rate 1 (%) (Optional)</label>
                    <input
                      type="text"
                      value={formData.interest1}
                      onChange={(e) => setFormData({...formData, interest1: e.target.value})}
                      placeholder="e.g. 9.50"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Interest Rate 2 (%) (Optional)</label>
                    <input
                      type="text"
                      value={formData.interest2}
                      onChange={(e) => setFormData({...formData, interest2: e.target.value})}
                      placeholder="e.g. 10.50"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Icon</label>
                  <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
                    {iconOptions.map((icon) => (
                      <button
                        key={icon.name}
                        type="button"
                        onClick={() => setFormData({...formData, iconName: icon.name})}
                        className={`p-3 rounded-xl flex items-center justify-center transition-all ${
                          formData.iconName === icon.name 
                          ? 'bg-primary text-white shadow-md' 
                          : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                        }`}
                        title={icon.name}
                      >
                        <icon.component className="w-6 h-6" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Translation Fields Switcher Tab bar */}
              <div className="flex border-b border-gray-100">
                <button
                  type="button"
                  onClick={() => setLangTab('en')}
                  className={`px-6 py-3 font-bold text-sm border-b-2 transition-all ${
                    langTab === 'en'
                      ? 'border-secondary text-primary font-bold'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  English Fields
                </button>
                <button
                  type="button"
                  onClick={() => setLangTab('mr')}
                  className={`px-6 py-3 font-bold text-sm border-b-2 transition-all ${
                    langTab === 'mr'
                      ? 'border-secondary text-primary font-bold'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  Marathi (मराठी) Fields
                </button>
              </div>

              {/* Dynamic langTab Switch Content */}
              {langTab === 'en' ? (
                // ENGLISH FIELDS TAB
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Scheme Title (English)</label>
                      <input
                        type="text"
                        required
                        value={formData.titleEn}
                        onChange={(e) => setFormData({...formData, titleEn: e.target.value})}
                        placeholder="e.g. Fixed Deposit (FD)"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Time Period 1 (English) (Optional)</label>
                      <input
                        type="text"
                        value={formData.period1En}
                        onChange={(e) => setFormData({...formData, period1En: e.target.value})}
                        placeholder="e.g. 1 to 5 Years"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Time Period 2 (English) (Optional)</label>
                      <input
                        type="text"
                        value={formData.period2En}
                        onChange={(e) => setFormData({...formData, period2En: e.target.value})}
                        placeholder="e.g. 5+ Years"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Description (English)</label>
                      <textarea
                        rows="3"
                        value={formData.descEn}
                        onChange={(e) => setFormData({...formData, descEn: e.target.value})}
                        placeholder="Brief overview of the scheme in English..."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium resize-none"
                      ></textarea>
                    </div>

                    <div className="space-y-3 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Key Features (English)</label>
                        <button 
                          type="button" 
                          onClick={addFeatureField}
                          className="text-primary text-sm font-bold flex items-center gap-1 hover:underline"
                        >
                          <Plus className="w-4 h-4" /> Add Feature
                        </button>
                      </div>
                      
                      {formData.featuresEn.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <div className="bg-gray-100 p-3 rounded-xl flex items-center justify-center text-gray-400">
                            {index + 1}
                          </div>
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureEnChange(index, e.target.value)}
                            placeholder={`Feature ${index + 1} in English`}
                            className="flex-grow px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                          />
                          {formData.featuresEn.length > 1 && (
                            <button 
                              type="button"
                              onClick={() => removeFeatureField(index)}
                              className="p-3 bg-red-50 text-red-500 hover:bg-red-100 rounded-xl transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                  </div>
                </div>
              ) : (
                // MARATHI FIELDS TAB
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Scheme Title (Marathi)</label>
                      <input
                        type="text"
                        required
                        value={formData.titleMr}
                        onChange={(e) => setFormData({...formData, titleMr: e.target.value})}
                        placeholder="उदा. मुदत ठेव (FD)"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Time Period 1 (Marathi) (Optional)</label>
                      <input
                        type="text"
                        value={formData.period1Mr}
                        onChange={(e) => setFormData({...formData, period1Mr: e.target.value})}
                        placeholder="उदा. १ ते ५ वर्षे"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Time Period 2 (Marathi) (Optional)</label>
                      <input
                        type="text"
                        value={formData.period2Mr}
                        onChange={(e) => setFormData({...formData, period2Mr: e.target.value})}
                        placeholder="उदा. ५+ वर्षे"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Description (Marathi)</label>
                      <textarea
                        rows="3"
                        value={formData.descMr}
                        onChange={(e) => setFormData({...formData, descMr: e.target.value})}
                        placeholder="योजनेचा संक्षिप्त आढावा मराठीत..."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium resize-none"
                      ></textarea>
                    </div>

                    <div className="space-y-3 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Key Features (Marathi)</label>
                        <button 
                          type="button" 
                          onClick={addFeatureField}
                          className="text-primary text-sm font-bold flex items-center gap-1 hover:underline"
                        >
                          <Plus className="w-4 h-4" /> Add Feature
                        </button>
                      </div>
                      
                      {formData.featuresMr.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <div className="bg-gray-100 p-3 rounded-xl flex items-center justify-center text-gray-400">
                            {index + 1}
                          </div>
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureMrChange(index, e.target.value)}
                            placeholder={`वैशिष्ट्य ${index + 1} मराठीत`}
                            className="flex-grow px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                          />
                          {formData.featuresMr.length > 1 && (
                            <button 
                              type="button"
                              onClick={() => removeFeatureField(index)}
                              className="p-3 bg-red-50 text-red-500 hover:bg-red-100 rounded-xl transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                  </div>
                </div>
              )}

              <div className="pt-6 flex justify-end gap-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary px-8 flex items-center gap-2"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
                  {editingId ? 'Update Scheme' : 'Create Scheme'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DepositSchemesManager;
